/**
 * Case retention management.
 * - Tracks which cases are accessed locally
 * - Pins newly created cases (never auto-deleted)
 * - Prunes old cases to keep storage manageable
 */

import type { SQLocalDrizzle } from "sqlocal/drizzle";
import { getSqlocal as getDbSqlocal } from "./db";
import { opfsDelete } from "./blobs";

// Cache the sqlocal instance - set during initCaseRetention
let _sqlocal: SQLocalDrizzle | null = null;

function getSqlocal(): SQLocalDrizzle {
  if (_sqlocal) return _sqlocal;
  return getDbSqlocal();
}

const MAX_RETAINED_CASES = 50;

export interface CaseRetentionInfo {
  caseId: string;
  pinned: boolean;
  accessedAt: number;
}

/**
 * Mark a case as accessed. Called when viewing or editing a case.
 */
export async function markCaseAccessed(caseId: string): Promise<void> {
  const sqlocal = getSqlocal();
  await sqlocal.sql`
    INSERT INTO local_cases (case_id, pinned, accessed_at)
    VALUES (${caseId}, 0, ${Date.now()})
    ON CONFLICT(case_id) DO UPDATE SET accessed_at = ${Date.now()}
  `;
}

// Alias for markCaseAccessed
export const touchCase = markCaseAccessed;

/**
 * Pin a case (mark as locally created, never auto-delete).
 */
export async function pinCase(caseId: string): Promise<void> {
  const sqlocal = getSqlocal();
  await sqlocal.sql`
    INSERT INTO local_cases (case_id, pinned, accessed_at)
    VALUES (${caseId}, 1, ${Date.now()})
    ON CONFLICT(case_id) DO UPDATE SET pinned = 1, accessed_at = ${Date.now()}
  `;
}

/**
 * Unpin a case (allow it to be pruned).
 */
export async function unpinCase(caseId: string): Promise<void> {
  const sqlocal = getSqlocal();
  await sqlocal.sql`
    UPDATE local_cases SET pinned = 0 WHERE case_id = ${caseId}
  `;
}

/**
 * Get list of retained cases.
 */
export async function getRetainedCases(): Promise<CaseRetentionInfo[]> {
  const sqlocal = getSqlocal();
  const rows = (await sqlocal.sql`
    SELECT case_id, pinned, accessed_at FROM local_cases ORDER BY accessed_at DESC
  `) as any[];

  return rows.map((r) => ({
    caseId: r.case_id,
    pinned: r.pinned === 1,
    accessedAt: r.accessed_at,
  }));
}

/**
 * Get case IDs that should be kept locally.
 * Returns pinned cases + last N cases by update date.
 * Merges cases table (synced data) with local_cases (bootstrap before sync).
 */
export async function getCasesToKeep(): Promise<Set<string>> {
  const sqlocal = getSqlocal();
  // Get all pinned cases
  const pinned = (await sqlocal.sql`
    SELECT case_id FROM local_cases WHERE pinned = 1
  `) as { case_id: string }[];

  // Merge both sources: synced cases by updated_at + local_cases by accessed_at
  // (local_cases covers bootstrap: server sends recentCaseIds before sync)
  const recent = (await sqlocal.sql`
    SELECT id, ts FROM (
      SELECT id, updated_at AS ts FROM cases
      UNION ALL
      SELECT case_id AS id, accessed_at AS ts FROM local_cases WHERE pinned = 0
    ) GROUP BY id
    ORDER BY MAX(ts) DESC
    LIMIT ${MAX_RETAINED_CASES}
  `) as { id: string }[];

  const keep = new Set<string>();
  for (const r of pinned) keep.add(r.case_id);
  for (const r of recent) keep.add(r.id);
  return keep;
}

/**
 * Get case IDs that should be pruned.
 */
async function getCasesToPrune(): Promise<string[]> {
  const sqlocal = getSqlocal();
  const keep = await getCasesToKeep();

  // Get all case IDs stored locally (union of local_cases tracking table and cases projection)
  const all = (await sqlocal.sql`
    SELECT case_id AS id FROM local_cases
    UNION
    SELECT id FROM cases
  `) as { id: string }[];

  return all.filter((r) => !keep.has(r.id)).map((r) => r.id);
}

/**
 * Delete all local data for a case: projections, events, and entity hashes.
 * This fully removes the case from the local database so it won't appear
 * in future Merkle tree comparisons.
 */
async function deleteCaseData(caseId: string): Promise<void> {
  const sqlocal = getSqlocal();

  // Collect all entity IDs belonging to this case
  const entityIds: string[] = [caseId];

  // Get patient ID before deleting the case
  const caseRows =
    (await sqlocal.sql`SELECT patient_id FROM cases WHERE id = ${caseId}`) as {
      patient_id: string;
    }[];
  if (caseRows.length > 0 && caseRows[0].patient_id) {
    entityIds.push(caseRows[0].patient_id);
  }

  // Collect sub-entity IDs from each table
  for (const rows of [
    (await sqlocal.sql`SELECT id FROM case_vitals WHERE case_id = ${caseId}`) as {
      id: string;
    }[],
    (await sqlocal.sql`SELECT id FROM case_ventilation WHERE case_id = ${caseId}`) as {
      id: string;
    }[],
    (await sqlocal.sql`SELECT id FROM case_diagnostics WHERE case_id = ${caseId}`) as {
      id: string;
    }[],
    (await sqlocal.sql`SELECT id FROM case_lab_values WHERE case_id = ${caseId}`) as {
      id: string;
    }[],
    (await sqlocal.sql`SELECT id FROM case_exam WHERE case_id = ${caseId}`) as {
      id: string;
    }[],
    (await sqlocal.sql`SELECT id FROM case_cpr WHERE case_id = ${caseId}`) as {
      id: string;
    }[],
    (await sqlocal.sql`SELECT id FROM case_tourniquet WHERE case_id = ${caseId}`) as {
      id: string;
    }[],
    (await sqlocal.sql`SELECT id FROM case_medication WHERE case_id = ${caseId}`) as {
      id: string;
    }[],
    (await sqlocal.sql`SELECT id FROM case_surgery WHERE case_id = ${caseId}`) as {
      id: string;
    }[],
    (await sqlocal.sql`SELECT id FROM attachments WHERE case_id = ${caseId}`) as {
      id: string;
    }[],
  ]) {
    for (const row of rows) entityIds.push(row.id);
  }

  // Delete projections from all case-related tables
  await sqlocal.sql`DELETE FROM case_vitals WHERE case_id = ${caseId}`;
  await sqlocal.sql`DELETE FROM case_ventilation WHERE case_id = ${caseId}`;
  await sqlocal.sql`DELETE FROM case_diagnostics WHERE case_id = ${caseId}`;
  await sqlocal.sql`DELETE FROM case_lab_values WHERE case_id = ${caseId}`;
  await sqlocal.sql`DELETE FROM case_exam WHERE case_id = ${caseId}`;
  await sqlocal.sql`DELETE FROM case_cpr WHERE case_id = ${caseId}`;
  await sqlocal.sql`DELETE FROM case_tourniquet WHERE case_id = ${caseId}`;
  await sqlocal.sql`DELETE FROM case_medication WHERE case_id = ${caseId}`;
  await sqlocal.sql`DELETE FROM case_surgery WHERE case_id = ${caseId}`;
  await sqlocal.sql`DELETE FROM attachments WHERE case_id = ${caseId}`;
  await sqlocal.sql`DELETE FROM cases WHERE id = ${caseId}`;
  await sqlocal.sql`DELETE FROM local_cases WHERE case_id = ${caseId}`;

  // Delete events and entity hashes for all collected entity IDs
  for (const eid of entityIds) {
    await sqlocal.sql`DELETE FROM events WHERE entity_id = ${eid}`;
    await sqlocal.sql`DELETE FROM entity_hashes WHERE entity_id = ${eid}`;
  }
}

/**
 * Prune old cases from local storage.
 * Keeps pinned cases + last N accessed.
 * Returns number of cases pruned.
 */
export async function pruneCases(): Promise<number> {
  const toPrune = await getCasesToPrune();
  if (toPrune.length === 0) return 0;

  // Collect blob sha256s from cases being pruned
  const sqlocal = getSqlocal();
  const prunedShas = new Set<string>();
  for (const caseId of toPrune) {
    const rows =
      (await sqlocal.sql`SELECT sha256 FROM attachments WHERE case_id = ${caseId} AND sha256 IS NOT NULL`) as {
        sha256: string;
      }[];
    for (const r of rows) prunedShas.add(r.sha256);
  }

  // Delete case data
  for (const caseId of toPrune) {
    await deleteCaseData(caseId);
  }

  // GC: only delete blobs not referenced by any remaining attachment
  if (prunedShas.size > 0) {
    const surviving =
      (await sqlocal.sql`SELECT DISTINCT sha256 FROM attachments WHERE sha256 IS NOT NULL`) as {
        sha256: string;
      }[];
    const survivingSet = new Set(surviving.map((r) => r.sha256));
    // Also keep blobs still in the upload queue
    const pending = (await sqlocal.sql`SELECT sha256 FROM pending_blobs`) as {
      sha256: string;
    }[];
    for (const r of pending) survivingSet.add(r.sha256);

    for (const sha of prunedShas) {
      if (!survivingSet.has(sha)) {
        await opfsDelete(sha);
      }
    }
  }

  return toPrune.length;
}

/**
 * Get retention stats.
 */
export async function getRetentionStats(): Promise<{
  total: number;
  pinned: number;
  recent: number;
  pruneable: number;
}> {
  const sqlocal = getSqlocal();
  const all =
    (await sqlocal.sql`SELECT COUNT(*) as count FROM cases`) as {
      count: number;
    }[];
  const pinned =
    (await sqlocal.sql`SELECT COUNT(*) as count FROM local_cases WHERE pinned = 1`) as {
      count: number;
    }[];

  const total = all[0]?.count ?? 0;
  const pinnedCount = pinned[0]?.count ?? 0;
  const recentCount = Math.min(total - pinnedCount, MAX_RETAINED_CASES);
  const pruneable = Math.max(0, total - pinnedCount - MAX_RETAINED_CASES);

  return {
    total,
    pinned: pinnedCount,
    recent: recentCount,
    pruneable,
  };
}

/**
 * Initialize case retention from existing cases in database.
 * Called during database initialization - sqlocal passed to avoid circular dep.
 */
export async function initCaseRetention(
  sqlocal: SQLocalDrizzle,
): Promise<void> {
  // Cache it for later use by other functions
  _sqlocal = sqlocal;

  // Get all cases currently in local DB that aren't tracked yet
  const existingCases = (await sqlocal.sql`
    SELECT id, updated_at FROM cases WHERE id NOT IN (SELECT case_id FROM local_cases)
  `) as { id: string; updated_at: number }[];

  // Add them to local_cases
  for (const c of existingCases) {
    await sqlocal.sql`
      INSERT OR IGNORE INTO local_cases (case_id, pinned, accessed_at)
      VALUES (${c.id}, 0, ${c.updated_at})
    `;
  }
}
